<script setup lang="ts">
import { SpeckleConfigurationType } from "@/features/BuildingProject";
import NewSpeckleChild from "@/types/Views/MappingWizard/NewSpeckleChild";
import { DeclaredUnitEnum } from "@/types/Views/MaterialDatabase/MaterialEnums";

import TextInput from "@/components/Reusables/Inputs/TextInput.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import DeclaredUnitSelect from "./DeclaredUnitSelect.vue";
import BimCodeSelect from "./BimCodeSelect.vue";
import { Trash2 } from "@lucide/vue";
import { computed, watch } from "vue";

const props = defineProps<{
  child: NewSpeckleChild;
  byType: boolean;
  index: number;
  declaredUnitEnums: { [key: string]: DeclaredUnitEnum };
  hasValidated: boolean;
  speckleConfiguration: SpeckleConfigurationType;
}>();

const emits = defineEmits<{
  onRemove: [];
  formInvalid: [invalid: boolean];
  handleBimCodeSelect: [code: string];
}>();

const handleBimCodeSelect = (val: string) => {
  props.child.speckleUserParameters.CCSClassification = val;
  emits("handleBimCodeSelect", val);
};

const onCCSClassification = (classification: any) => {
  const { CCSClassification, classificationObjectClass } = classification;
  props.child.speckleUserParameters.CCSClassification = CCSClassification;
  props.child.speckleUserParameters.classificationObjectClass = classificationObjectClass;
};

const validateUnitValue = computed(() => {
  const value = props.child.declaredUnitValuePairs[0]?.value;

  if (value === null || value === undefined || value < 0) {
    return "Please enter a valid unit value";
  }

  return undefined;
});

const validateIdentifier = computed(() => {
  const value = props.child.typeIdentifier.length === 0;

  if (value) {
    return "Please enter a name";
  }

  return undefined;
});

const validateClassification = computed(() => {
  const value = props.child.speckleUserParameters.CCSClassification.length === 0;

  if (value) {
    return "Please enter a name";
  }

  return undefined;
});

watch(
  validateUnitValue,
  (errorMsg) => {
    if (errorMsg) {
      emits("formInvalid", true);
    } else {
      emits("formInvalid", false);
    }
  },
  { immediate: true }
);

watch(
  validateIdentifier,
  (errorMsg) => {
    if (errorMsg) {
      emits("formInvalid", true);
    } else {
      emits("formInvalid", false);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="gap-px-8 grid grid-cols-18 px-4 py-3">
    <div class="col-span-6">
      <TextInput
        v-model:value="child.typeIdentifier"
        color="primary"
        placeholder="Type name"
        :error-msg="hasValidated ? validateIdentifier : ''" />
    </div>

    <div class="col-span-3">
      <DeclaredUnitSelect
        :declared-unit-enums="declaredUnitEnums"
        :material="child"
        :error-msg="hasValidated && child.declaredUnitValuePairs[0].declaredUnit === -1 ? ' ' : undefined" />
    </div>

    <div class="col-span-3">
      <NumberInput
        v-model:value="child.declaredUnitValuePairs[0].value"
        color="primary"
        placeholder="Value"
        :error-msg="hasValidated ? validateUnitValue : ''" />
    </div>
    <div class="col-span-5">
      <BimCodeSelect
        required
        :bim-type-code="child.speckleUserParameters.BIMTypeCode"
        :ccs-classification="child.speckleUserParameters[speckleConfiguration.parameters.classificationCode]"
        :classification-object-class="child.speckleUserParameters.classificationObjectClass"
        :error-msg="hasValidated ? validateClassification : ''"
        @value-change="handleBimCodeSelect"
        @on-c-c-s-classification="onCCSClassification" />
    </div>

    <div class="col-span-1">
      <div class="flex h-full items-center justify-center">
        <button
          v-if="index > 0"
          v-tooltip="{ text: 'Remove type', variant: 'label', placement: 'top' }"
          type="button"
          class="focus-ring interactive-active duration-fast ease-standard rounded-4 text-type-muted hover:bg-app-red-50 hover:text-app-red-600 p-1 transition-colors"
          aria-label="Remove type"
          @click="emits('onRemove')">
          <Trash2
            class="icon-sm"
            :stroke-width="1.5"
            aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
